import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, X, ChevronUp, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const QuestionBankDropdown = ({
  selectedBankIds = [],
  onSelectionChange,
  userQuestionBanks = [],
  companyQuestionBanks = [],
  placeholder = "Select Question Banks...",
  className,
  disabled = false,
  multiple = true,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const scrollRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollIntervalRef = useRef(null);

  // Filter out AI Generated question banks
  const filterAIGeneratedBanks = (banks) => {
    return banks.filter(bank => 
      !bank.name.toLowerCase().includes('ai generated') &&
      !bank.name.toLowerCase().includes('ai-generated') &&
      !bank.isAIGenerated
    );
  };

  const filteredUserQuestionBanks = filterAIGeneratedBanks(userQuestionBanks);
  const filteredCompanyQuestionBanks = filterAIGeneratedBanks(companyQuestionBanks);

  // Filter banks based on search term
  const filterBanksBySearch = (banks) => {
    if (!searchTerm) return banks;
    return banks.filter(bank => 
      bank.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const searchFilteredUserBanks = filterBanksBySearch(filteredUserQuestionBanks);
  const searchFilteredCompanyBanks = filterBanksBySearch(filteredCompanyQuestionBanks);

  // Check scroll position and update button visibility
  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setCanScrollUp(scrollTop > 0);
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1);
    }
  };

  // Update scroll buttons when content changes
  useEffect(() => {
    if (open) {
      setTimeout(updateScrollButtons, 100); // Small delay to ensure content is rendered
    }
  }, [open, searchFilteredUserBanks, searchFilteredCompanyBanks, searchTerm]);

  // Cleanup scroll interval on unmount and when dropdown closes
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, []);

  // Stop scrolling when dropdown closes
  useEffect(() => {
    if (!open) {
      stopScrolling();
    }
  }, [open]);

  const handleSelect = (bankId) => {
    if (multiple) {
      const updatedSelection = selectedBankIds.includes(bankId)
        ? selectedBankIds.filter((id) => id !== bankId)
        : [...selectedBankIds, bankId];
      onSelectionChange(updatedSelection);
    } else {
      onSelectionChange(selectedBankIds.includes(bankId) ? [] : [bankId]);
      setOpen(false);
    }
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  const getSelectedBanks = () => {
    const allBanks = [...filteredUserQuestionBanks, ...filteredCompanyQuestionBanks];
    return allBanks.filter((bank) => selectedBankIds.includes(bank._id));
  };

  const selectedBanks = getSelectedBanks();

  // Scroll functions with proper behavior
  const scrollUp = () => {
    if (scrollRef.current && canScrollUp) {
      scrollRef.current.scrollBy({ top: -30, behavior: 'smooth' });
      setTimeout(updateScrollButtons, 100);
    }
  };

  const scrollDown = () => {
    if (scrollRef.current && canScrollDown) {
      scrollRef.current.scrollBy({ top: 30, behavior: 'smooth' });
      setTimeout(updateScrollButtons, 100);
    }
  };

  // Improved hover to scroll functionality with better state management
  const startScrolling = (direction) => {
    // Clear any existing interval first
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    
    const scrollFn = direction === 'up' ? scrollUp : scrollDown;
    const canScroll = direction === 'up' ? canScrollUp : canScrollDown;
    
    // Only start if we can actually scroll in that direction
    if (!canScroll) return;
    
    // Initial scroll immediately
    scrollFn();
    
    // Set up continuous scrolling with more frequent updates
    scrollIntervalRef.current = setInterval(() => {
      const currentCanScroll = direction === 'up' ? 
        (scrollRef.current && scrollRef.current.scrollTop > 0) :
        (scrollRef.current && scrollRef.current.scrollTop < scrollRef.current.scrollHeight - scrollRef.current.clientHeight - 1);
      
      if (currentCanScroll) {
        scrollFn();
      } else {
        // Stop scrolling if we can't scroll anymore
        stopScrolling();
      }
    }, 80); // Faster interval for smoother scrolling
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // Enhanced mouse event handlers to prevent sticking
  const handleMouseEnter = (direction) => {
    // Small delay to ensure proper state
    setTimeout(() => {
      startScrolling(direction);
    }, 50);
  };

  const handleMouseLeave = () => {
    stopScrolling();
    // Update scroll buttons after stopping
    setTimeout(updateScrollButtons, 100);
  };

  // Handle scroll event
  const handleScroll = () => {
    updateScrollButtons();
  };

  // For single selection, use the exact same Select component as exam selection
  if (!multiple) {
    return (
      <div className={className}>
        <Select
          value={selectedBankIds[0] || ""}
          onValueChange={(value) => onSelectionChange(value ? [value] : [])}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {searchFilteredUserBanks.length > 0 && (
              <SelectGroup>
                <SelectLabel>Your Question Banks</SelectLabel>
                {searchFilteredUserBanks.map((bank) => (
                  <SelectItem key={bank._id} value={bank._id}>
                    <div className="flex flex-col py-2 min-h-[3rem] justify-center">
                      <div className="font-medium leading-relaxed whitespace-normal break-words">{bank.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">({bank.questionCount} questions)</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}

            {searchFilteredUserBanks.length > 0 && searchFilteredCompanyBanks.length > 0 && (
              <SelectSeparator />
            )}

            {searchFilteredCompanyBanks.length > 0 && (
              <SelectGroup>
                <SelectLabel>Company Question Banks</SelectLabel>
                {searchFilteredCompanyBanks.map((bank) => (
                  <SelectItem key={bank._id} value={bank._id}>
                    <div className="flex flex-col py-2 min-h-[3rem] justify-center">
                      <div className="font-medium leading-relaxed whitespace-normal break-words">{bank.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">({bank.questionCount} questions)</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // For multiple selection, use Popover with exact SelectContent structure
  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between min-h-10 h-auto py-2"
            disabled={disabled}
          >
            <div className="flex flex-wrap gap-1 max-w-[calc(100%-40px)] items-start">
              {selectedBanks.length === 0 ? (
                <span className="text-muted-foreground py-1">{placeholder}</span>
              ) : (
                <>
                  {selectedBanks.slice(0, 1).map((bank) => (
                    <Badge
                      key={bank._id}
                      variant="secondary"
                      className="text-xs max-w-full whitespace-normal break-words leading-relaxed py-1 px-2 h-auto"
                      title={bank.name}
                    >
                      {bank.name}
                    </Badge>
                  ))}
                  {selectedBanks.length > 1 && (
                    <Badge variant="secondary" className="text-xs py-1">
                      +{selectedBanks.length - 1} more
                    </Badge>
                  )}
                </>
              )}
            </div>
            <div className="flex items-start gap-1 shrink-0 ml-2 mt-1">
              {selectedBanks.length > 0 && (
                <X 
                  className="h-4 w-4 opacity-50 hover:opacity-100" 
                  onClick={clearSelection}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-full p-0" 
          style={{ width: "max(var(--radix-popover-trigger-width), 320px)" }}
          side="bottom"
          align="start"
          sideOffset={4}
          onMouseLeave={handleMouseLeave} // Stop scrolling if mouse leaves entire dropdown
        >
          {/* Use the exact same structure as SelectContent with functional scroll buttons */}
          <div className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
            {/* Up Scroll Button - Only show when can scroll up */}
            {canScrollUp && (
              <div 
                className="flex cursor-pointer items-center justify-center py-1 hover:bg-accent transition-colors border-b"
                onClick={scrollUp}
                onMouseEnter={() => handleMouseEnter('up')}
                onMouseLeave={handleMouseLeave}
                onMouseDown={(e) => e.preventDefault()} // Prevent text selection
              >
                <ChevronUp className="h-4 w-4" />
              </div>
            )}
            
            {/* Viewport - Proper structure to prevent content overflow */}
            <div 
              ref={scrollRef}
              className="max-h-80 overflow-y-auto scrollbar-hidden"
              onScroll={handleScroll}
            >
              <div className="p-1">
                {/* Search Input */}
                <div className="p-2 border-b mb-1 sticky top-0 bg-popover z-10">
                  <Input
                    placeholder="Search question banks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-8"
                  />
                </div>

              {/* User Question Banks */}
              {searchFilteredUserBanks.length > 0 && (
                <>
                  {searchFilteredUserBanks.map((bank) => (
                    <div
                      key={bank._id}
                      onClick={() => handleSelect(bank._id)}
                      className="relative flex w-full cursor-default select-none items-start rounded-sm py-3 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer min-h-[3rem]"
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center mt-1">
                        <Check
                          className={cn(
                            "h-4 w-4",
                            selectedBankIds.includes(bank._id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium leading-relaxed pr-2 whitespace-normal break-words" title={bank.name}>
                          {bank.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {bank.questionCount} questions
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Separator */}
              {searchFilteredUserBanks.length > 0 && searchFilteredCompanyBanks.length > 0 && (
                <div className="-mx-1 my-1 h-px bg-muted" />
              )}

              {/* Company Question Banks */}
              {searchFilteredCompanyBanks.length > 0 && (
                <>
                  <div className="py-1.5 pl-8 pr-2 text-sm font-semibold">Company Question Banks</div>
                  {searchFilteredCompanyBanks.map((bank) => (
                    <div
                      key={bank._id}
                      onClick={() => handleSelect(bank._id)}
                      className="relative flex w-full cursor-default select-none items-start rounded-sm py-3 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer min-h-[3rem]"
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center mt-1">
                        <Check
                          className={cn(
                            "h-4 w-4",
                            selectedBankIds.includes(bank._id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium leading-relaxed pr-2 whitespace-normal break-words" title={bank.name}>
                          {bank.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {bank.questionCount} questions
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* No Results */}
              {searchFilteredUserBanks.length === 0 && searchFilteredCompanyBanks.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {searchTerm ? "No question banks found." : "No question banks available."}
                </div>
              )}
              </div>
            </div>
            
            {/* Down Scroll Button - Only show when can scroll down */}
            {canScrollDown && (
              <div 
                className="flex cursor-pointer items-center justify-center py-1 hover:bg-accent transition-colors border-t"
                onClick={scrollDown}
                onMouseEnter={() => handleMouseEnter('down')}
                onMouseLeave={handleMouseLeave}
                onMouseDown={(e) => e.preventDefault()} // Prevent text selection
              >
                <ChevronDown className="h-4 w-4" />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default QuestionBankDropdown;
